// Surface-tile occupancy: which walker claims which cell of which face. The
// Unity original keeps per-block occupant lists (`BlockOccupant`,
// `AdaptBlockOccupationData` in `Assets/Code/BugsModel.cs`); this port needs
// only the surface half of that, so one flat map keyed by tile is enough.
//
// Pure logic: no WebGL, no DOM (00-architecture.md §1).

import { advance, poseToCell, FACE_IDS, N, type FaceId, type SurfacePose } from "./surface";
import { HALF_BODY } from "./defs";
// Type-only imports: erased at build time, so the bugs/spawners <-> occupancy
// cycles never exist at runtime.
import type { Cell } from "./blocks";
import type { BugId } from "./bugs";
import type { SpawnerId } from "./spawners";

/**
 * Everything that can hold a tile. Bugs and apples share one claim space —
 * that is what makes a bug brake for an apple without either system knowing
 * about the other. Ids are disjoint by construction: `BugManager` counts up
 * from 1, `SpawnerManager` counts down from -1.
 */
export type WalkerId = BugId | SpawnerId;

/** `face * N * N + floor(v) * N + floor(u)` — unique over the 6·N² tiles. */
export type TileKey = number;

/**
 * Surface coordinates run over the closed range [0, N]: a walker sitting
 * exactly on a face edge has `u === N`, which belongs to the last tile, not to
 * a seventh row that does not exist.
 */
function tileIndex(coord: number): number {
  const i = Math.floor(coord);
  return i < 0 ? 0 : i > N - 1 ? N - 1 : i;
}

export function tileKey(face: FaceId, u: number, v: number): TileKey {
  return (face * N + tileIndex(v)) * N + tileIndex(u);
}

/**
 * The grid cell behind a tile — the block a walker standing there rests on, and
 * so the block its claim protects from being repaired. Inverts `tileKey` and
 * runs the tile's centre through `poseToCell`; note the mapping is many-to-one,
 * because the two tiles either side of a cube edge wrap the same corner block.
 */
export function tileToCell(key: TileKey): Cell {
  const face = FACE_IDS[Math.floor(key / (N * N))];
  const rest = key - face * N * N;
  const v = Math.floor(rest / N);
  const c = poseToCell({ face, u: rest - v * N + 0.5, v: v + 0.5, dir: 0 });
  return [c.x, c.y, c.z];
}

/**
 * Exclusive tile claims. A walker holds its own tile and — while running — the
 * one it is entering, which is what stops two bugs from swapping through each
 * other in a single step.
 */
export class Occupancy {
  private readonly byTile = new Map<TileKey, WalkerId>();
  /** Reverse index so `releaseAll` costs the walker's claims, not the map. */
  private readonly byWalker = new Map<WalkerId, Set<TileKey>>();

  /** Take the tile; false (and no change) if someone else already holds it. */
  claim(key: TileKey, id: WalkerId): boolean {
    const holder = this.byTile.get(key);
    if (holder !== undefined && holder !== id) return false;
    this.byTile.set(key, id);
    let mine = this.byWalker.get(id);
    if (!mine) {
      mine = new Set();
      this.byWalker.set(id, mine);
    }
    mine.add(key);
    return true;
  }

  /** Drop the tile. Releasing someone else's claim is a no-op. */
  release(key: TileKey, id: WalkerId): void {
    if (this.byTile.get(key) !== id) return;
    this.byTile.delete(key);
    this.byWalker.get(id)?.delete(key);
  }

  holder(key: TileKey): WalkerId | null {
    return this.byTile.get(key) ?? null;
  }

  /** Every tile `id` currently holds. Empty for an unknown walker. */
  claimsOf(id: WalkerId): ReadonlySet<TileKey> {
    return this.byWalker.get(id) ?? EMPTY;
  }

  /** Drop all of a walker's claims — it died, or left the simulation. */
  releaseAll(id: WalkerId): void {
    const mine = this.byWalker.get(id);
    if (!mine) return;
    for (const key of mine) this.byTile.delete(key);
    this.byWalker.delete(id);
  }

  /**
   * Replace `id`'s claims with exactly `tiles`, but only if every one of them
   * is free or already its own; returns false and changes nothing otherwise.
   * This all-or-nothing form is what the movement system needs — a walker must
   * never end up holding half of the tiles its body spans.
   */
  reclaim(id: WalkerId, tiles: readonly TileKey[]): boolean {
    for (const key of tiles) {
      const holder = this.byTile.get(key);
      if (holder !== undefined && holder !== id) return false;
    }
    const mine = this.byWalker.get(id);
    if (mine) {
      for (const key of mine) {
        if (!tiles.includes(key)) this.byTile.delete(key);
      }
      mine.clear();
    }
    for (const key of tiles) this.claim(key, id);
    return true;
  }

  /**
   * Every walker whose claim covers a grid cell — the port of
   * `GetCubeWalkerBlockingPlaneRepair`, the gate `BlocksModel.RepairBlock`
   * refuses a repair on. Bugs *and* apples count: both share this claim space.
   *
   * A linear scan, as plan 08 allows: at most 6·N² = 150 tiles exist and only
   * the claimed ones are in the map, so a reverse index would cost more to keep
   * current than the walk saves on the handful of taps a second this sees.
   */
  holdersByCell(cell: Cell): WalkerId[] {
    const out: WalkerId[] = [];
    for (const [key, id] of this.byTile) {
      const c = tileToCell(key);
      if (c[0] === cell[0] && c[1] === cell[1] && c[2] === cell[2]) out.push(id);
    }
    return out;
  }

  /** Read-only view for debugging (`?debug=occupancy` in `main.ts`). */
  get tiles(): ReadonlyMap<TileKey, WalkerId> {
    return this.byTile;
  }
}

const EMPTY: ReadonlySet<TileKey> = new Set();

/**
 * The tiles a walker's body claims at `pose`: the one it stands on plus the one
 * its leading edge reaches into. Holding both is the invariant that stops two
 * walkers from swapping through each other within a single step — a swap would
 * need the tile between them claimed twice.
 */
export function bodyTiles(pose: SurfacePose): TileKey[] {
  const here = tileKey(pose.face, pose.u, pose.v);
  const lead = advance(pose, HALF_BODY);
  const ahead = tileKey(lead.face, lead.u, lead.v);
  return here === ahead ? [here] : [here, ahead];
}

/** A resting walker reaches into nothing; it holds only the tile it sits on. */
export function restingTile(pose: SurfacePose): TileKey[] {
  return [tileKey(pose.face, pose.u, pose.v)];
}
